import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Prism from 'prismjs';
import ReactSimpleCodeEditor from 'react-simple-code-editor';
import PlaygroundWrapper from './components/playground-wrapper';

const styles = `
  .__playground {
    --grid-outer-padding: 1em;
    position: relative;
    border-radius: 0.375em;
  }

  .__playground_btn {
    /* matching --blue-dark */
    background: hsl(206deg 65% 32%);
    font-size: inherit;
    color: white;
    border: none;
    padding: .25em .75em;
    border-radius: .25em;
    position: absolute;
    bottom: 1em;
    right: 1em;
    z-index: 1;
  }

  .__playground_btn:hover {
    background: hsl(206deg 65% 40%);
  }

  .__playground_btn:active {
    background: hsl(206deg 65% 25%);
  }

  .__playground_grid {
    display: grid;
    grid-gap: 1em;
  }

  .__playground_notSupported {
    border: 2px solid var(--red-dark);
    border-radius: 0.25rem;
    padding: 0.5em;
  }

  @media (min-width: 50em) {
    .__playground_grid {
      grid-template-columns: 3fr 2fr;
    }

    .__playground_grid.stacked {
      grid-template-columns: 1fr;
    }
  }

  .__playground textarea {
    outline: none;
  }

  .__playground_editor {
    margin: 0;
    border-radius: 0.25em;
    box-shadow: var(--soft-shadow-dark);
    background: var(--blue-darker);
  }

  .__playground_editor summary {
    padding: 0.5rem;
    color: var(--blue-bright);
    font-family: system-ui, sans-serif;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: 1px;
    cursor: pointer;
  }

  .__playground_editor:only-child {
    height: 100%;
  }

  .__playground_editor pre, .__playground_editor textarea {
    /* ugly fix for code golf spaceless code at /notes/code-golfing-at-its-best/ */
    margin: 0;
    word-break: break-all !important;
  }

  .__playground_editor:focus-within {
    outline: 2px solid var(--blue-bright);
  }

  .__playground_editor + .__playground_editor {
    margin-top: 1rem;
  }

  .__playground iframe {
    height: 100%;
    color-scheme: normal;
  }

  .__playground .preview {
    position: relative;
    text-align: right;
    font-size: .75em;
    letter-spacing: 1px;
    font-weight: 700;
    font-family: system-ui, sans-serif;
    color: var(--blue-bright);
    text-transform: uppercase;
    padding: 4em 1em 1em;
    background: #1a2932;
    box-shadow: var(--soft-shadow-dark);
    border-radius: .25em;
  }

  .__playground .prewiew__label {
    position: absolute;
    top: 1em;
    right: var(--grid-outer-padding);
  }

`;

const iframeBaseStyles = `
  html, body {
    font-family: sans-serif;
    font-size: 1em;
    margin: 0;
    overflow: hidden;
    background: transparent;
  }

  pre {
    margin: 0;
  }
`;

const Playground = function (props) {
  const {
    css: CSS,
    html: HTML,
    mode,
    previewheight,
    defaultOpen = {},
    previewBaseStyles = '',
  } = props;

  const [cssCode, setCssCode] = useState(CSS || '');
  const [htmlCode, setHtmlCode] = useState(HTML || '');
  const [wasEdited, setWasEdited] = useState(false);
  const [isLocal, setIsLocal] = useState(false);

  useEffect(() => {
    setIsLocal(window.location.hostname === 'localhost');
  }, []);

  return (
    <>
      <div className="__playground" {...props} style={{ display: 'grid' }}>
        {wasEdited ? (
          <button
            type="button"
            className="__playground_btn"
            onClick={() => {
              setCssCode(CSS);
              setHtmlCode(HTML);
              setWasEdited(false);
            }}
          >
            Revert your changes
          </button>
        ) : (
          ''
        )}
        <div
          className={`__playground_grid ${mode === 'STACKED' ? 'stacked' : ''}`}
        >
          <div>
            {HTML ? (
              <details
                className="__playground_editor"
                open={
                  typeof defaultOpen.html !== 'undefined'
                    ? defaultOpen.html
                    : true
                }
              >
                <summary>HTML</summary>
                <pre>
                  <ReactSimpleCodeEditor
                    value={htmlCode}
                    onValueChange={(code) => {
                      setWasEdited(true);
                      setHtmlCode(code);
                    }}
                    highlight={(code) =>
                      Prism.highlight(code, Prism.languages.html, 'html')
                    }
                    style={{
                      fontFamily: 'var(--coding-font)',
                      fontSize: '0.875em',
                    }}
                  />
                </pre>
              </details>
            ) : (
              ''
            )}

            {CSS ? (
              <details
                className="__playground_editor"
                open={
                  typeof defaultOpen.css !== 'undefined'
                    ? defaultOpen.css
                    : true
                }
              >
                <summary>CSS</summary>
                <pre>
                  <ReactSimpleCodeEditor
                    value={cssCode}
                    onValueChange={(code) => {
                      setWasEdited(true);
                      setCssCode(code);
                    }}
                    highlight={(code) =>
                      Prism.highlight(code, Prism.languages.css, 'css')
                    }
                    style={{
                      fontFamily: 'var(--coding-font)',
                      fontSize: '0.875em',
                    }}
                  />
                </pre>
              </details>
            ) : (
              ''
            )}
          </div>
          <div className="preview">
            <span className="prewiew__label">Preview</span>
            <iframe
              srcDoc={`<style>${iframeBaseStyles} ${previewBaseStyles} ${cssCode}</style>${htmlCode}`}
              style={{
                height: `${previewheight ? previewheight : '100%'}`,
              }}
            />
          </div>
        </div>
      </div>
      {isLocal ? (
        <div
          style={{
            padding: '1rem',
            marginTop: '1rem',
            border: '1px solid #555',
          }}
        >
          {JSON.stringify({ html: htmlCode, css: cssCode }, null, 2)}
        </div>
      ) : (
        ''
      )}
    </>
  );
};

const renderComponent = (props, container) => {
  const root = createRoot(container);
  root.render(React.createElement(Playground, props));
};

export { renderComponent, styles };
export default Playground;
